import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatHeader } from "blocks-nextjs/blocks-react/components/Header/Header";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconHelp } from "blocks-nextjs/blocks-react/components/Icons/Help";
import { CatIconPerson } from "blocks-nextjs/blocks-react/components/Icons/Person";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatUtilityNav } from "blocks-nextjs/blocks-react/components/UtilityNav/UtilityNav";
import { CatUtilityNavItem } from "blocks-nextjs/blocks-react/components/UtilityNavItem/UtilityNavItem";
import React, { useEffect, useState } from "react";
import { useIsRefreshedDashboardFlag } from "../../hooks/useAppConfigFlags";
import useAppConfigStore from "../../store/useAppConfigStore";
import { CatTextPassage } from "blocks-nextjs/blocks-react/components/TextPassage/TextPassage";
import { CatTooltip } from "blocks-nextjs/blocks-react/components/Tooltip/Tooltip";
import { matchPath } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import catLogo from "../../assets/CatLogo.svg";
import { CaasRoute } from "../../routes";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import NewProjectModal from "../CreateNewProject/NewProjectModal";
import { getCustomerList } from "../CustomerSearch/CustomerSearch";
import "./Header.css";
const Header = () => {
    const CUSTOMER_UCID_SELECTED = "partyNumber";
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isRefreshedDashboardEnabled = useIsRefreshedDashboardFlag();
    const isRestrictedPathActive = !!matchPath(pathname, "/restricted-access");
    const [isModalActive, setIsModalActive] = useState(false);
    const [isFuncCookieEnabled, setIsFuncCookieEnabled] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const { isLoggedInAsCAT, setUserPartyNumber, userPartyNumber } = useGlobalStore((state) => ({
        isLoggedInAsCAT: state.isLoggedInAsCAT,
        setUserPartyNumber: state.setUserPartyNumber,
        userPartyNumber: state.userPartyNumber
    }));
    const [partyNumber, setPartyNumber] = useState(localStorage.getItem(CUSTOMER_UCID_SELECTED) || userPartyNumber);
    // CustomerSearch can change the selection while this header stays mounted.
    useEffect(() => {
        setPartyNumber(localStorage.getItem(CUSTOMER_UCID_SELECTED) || userPartyNumber);
    }, [userPartyNumber]);
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { projectAddress, projectName, projectOwner } = useNewProjectStore((state) => ({
        projectAddress: state.projectAddress,
        projectName: state.projectName,
        projectOwner: state.projectOwner
    }));
    const checkNewProjectPathAndRenderModal = () => {
        const isCreateNewProjectPathActive = !!matchPath(pathname, "/new-project");
        if (isCreateNewProjectPathActive && projectName && projectOwner && projectAddress) {
            setIsModalActive(true);
            return true;
        }
        return false;
    };
    const handleLogoClick = () => {
        if (checkNewProjectPathAndRenderModal()) {
            return;
        }
        if (isRestrictedPathActive) {
            return;
        }
        // The project Live-tab redirect is enabled only with the refreshed dashboard rollout.
        const projectDetailsMatch = matchPath(CaasRoute.ProjectDetails, pathname);
        if (isRefreshedDashboardEnabled && projectDetailsMatch?.params.id) {
            navigate(`${CaasRoute.ProjectDetails.replace(":id", projectDetailsMatch.params.id)}?tab=Live`);
            return;
        }
        navigate(CaasRoute.Home);
    };
    function extractPartyNumberFromSelectedCustomer(selectedCustomer) {
        const match = selectedCustomer.match(/\d+/);
        return match ? match[0] : selectedCustomer;
    }
    const handleSelectCustomer = (selectedCustomer) => {
        setPartyNumber(selectedCustomer);
        setUserPartyNumber(extractPartyNumberFromSelectedCustomer(selectedCustomer));
        localStorage.setItem(CUSTOMER_UCID_SELECTED, selectedCustomer);
        // The current project belongs to the previous customer, so land on that customer's project list.
        navigate(CaasRoute.Home);
    };
    useEffect(() => {
        const checkFuncCookie = () => {
            //using for OneTrust Functional Cookie enable/disable
            if (window.OnetrustActiveGroups && window.OnetrustActiveGroups.includes("C0003")) {
                setIsFuncCookieEnabled(true);
            }
            else {
                setIsFuncCookieEnabled(false);
            }
        };
        checkFuncCookie();
        window.addEventListener("OneTrustGroupsUpdated", checkFuncCookie);
        return () => {
            window.removeEventListener("OneTrustGroupsUpdated", checkFuncCookie);
        };
    }, []);
    const handleHelpClick = () => {
        if (!isFuncCookieEnabled) {
            setShowTooltip(!showTooltip);
        }
    };
    const openCookieSettings = () => {
        setShowTooltip(false);
        if (window.OneTrust) {
            window.OneTrust.ToggleInfoDisplay();
        }
    };
    // partyNumber holds the full "Name (id)" string once a customer has been selected.
    const customerNameMatch = partyNumber.match(/^(.*)\s\((.*)\)$/);
    const customerDisplayName = customerNameMatch ? customerNameMatch[1] : partyNumber;
    const customerDisplayId = customerNameMatch ? customerNameMatch[2] : "";
    const customerOptions = getCustomerList(appConfig)
        .filter((customer) => customer !== partyNumber)
        .sort((a, b) => a.localeCompare(b));
    // Show the refreshed customer selector in the navbar on every page for CAT users.
    const showCustomerSelector = isRefreshedDashboardEnabled && isLoggedInAsCAT;
    return (_jsxs(CatHeader, { "data-test": "cat-header", children: [_jsx("img", { className: "mx-4 cursor-pointer h-[32px] w-[60px]", "data-test": "cat-logo", onClick: handleLogoClick, src: catLogo }), _jsxs("div", { className: showCustomerSelector ? "ms-auto flex items-center gap-10" : "contents", "data-test": "header-right-section", children: [!showCustomerSelector && isLoggedInAsCAT && (_jsxs("div", { className: "ml-auto font-bold text-white", children: ["Selected Customer: ", localStorage.getItem(CUSTOMER_UCID_SELECTED)] })), showCustomerSelector && (_jsxs("div", { className: "flex items-center gap-3", "data-test": "header-customer-selector", children: [_jsx("span", { className: "text-sm text-[#e1e1e1]", "data-test": "header-customer-label", children: "Customer" }), _jsxs("div", { className: "relative w-[292px]", "data-test": "header-customer-dropdown-wrapper", children: [_jsxs(CatDropdown, { arialabel: "Selected customer", className: "header-customer-dropdown w-full", "data-test": "header-customer-dropdown", fieldId: "header-customer-dropdown", size: "sm", value: partyNumber, children: [_jsxs("span", { className: "flex min-w-0 items-center", slot: "before", title: partyNumber, children: [_jsx("span", { className: "truncate text-base font-semibold leading-6 text-white", "data-test": "header-customer-name", children: customerDisplayName }), customerDisplayId && (_jsxs("span", { className: "ml-1 truncate text-base leading-6 text-[#aaa]", "data-test": "header-customer-id", children: ["(", customerDisplayId, ")"] }))] }), _jsx("div", { className: "header-customer-dropdown-list-scroll max-h-[160px] overflow-y-auto rounded-t-[8px]", children: _jsx(CatList, { "data-test": "header-customer-list", children: customerOptions.map((customer) => (_jsx(CatListItem, { "data-test": "header-customer-list-item", onBlSelect: () => handleSelectCustomer(customer), title: customer, value: { label: customer, value: customer } }, customer))) }) })] }), _jsx(CatIconChevronDown, { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-white", color: "knockout", "data-test": "header-customer-chevron" })] })] })), _jsxs(CatUtilityNav, { children: [appConfig?.release_1_1?.showWhatFix && (_jsxs("div", { className: "flex items-center text-white relative", "data-test": "guide-walkthrough", children: [_jsx(CatIconHelp, { className: "cat-icon pr-2 cursor-pointer", "data-test": "guided-tours", size: "lg" }), !isFuncCookieEnabled ? (_jsxs(CatTooltip, { isActive: showTooltip, isInteractive: true, position: "bottom", children: [_jsx("span", { className: "cursor-pointer text-sm", onClick: handleHelpClick, slot: "trigger", children: "Help center" }), _jsx(CatTextPassage, { size: "sm", children: _jsxs("span", { className: "text-gray-800 font-medium", children: ["Enable functional cookies to access Guided Tours. You can enable them in your\u00A0", _jsx("a", { className: "ot-sdk-show-settings hover:underline cursor-pointer text-blue-600", "data-test": "cookie-settings", onMouseDown: () => {
                                                                openCookieSettings();
                                                            }, children: "Cookie Settings." })] }) })] })) : (_jsx("span", { className: "cursor-pointer text-sm", children: "Help center" }))] })), _jsxs(CatUtilityNavItem, { "data-test": "cat-utility-nav-logout", dropdown: true, children: [_jsx(CatIconPerson, { className: "cat-icon", slot: "before" }), _jsx(CatList, { className: "c-top-nav__list", spacing: "padded", children: _jsx(CatListItem, { className: "c-top-nav__list-item", "data-test": "cat-utility-logout-option", onClick: () => navigate(CaasRoute.Logout), children: "Logout" }) })] })] })] }), _jsx(NewProjectModal, { onModalClose: (modelState) => {
                    setIsModalActive(modelState);
                }, showModal: isModalActive })] }));
};
export default Header;
